import React, {
  FunctionComponent,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { Container, makeStyles, IconButton } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import AssignProductsButton from 'components/@common/Buttons/AssignProductsButton';
import ProductIconList from 'components/@common/ProductIconList';
import StudentProductLicenseCard, {
  LicenseUpdate
} from 'components/@common/ProductLicenses/StudentProductLicenseCard';
import Table from 'components/@common/Table/StyledTable';
import useDefaultGroupSettings from 'components/ClassroomsModule/hooks/use-default-group-settings';
import { useFormikContext, FormikProps, FormikValues } from 'formik';
import { get } from 'lodash';
import useStore from 'store';
import { ProductWithCurriculum } from 'types';
import { OrganizationLicenseUsage, LicenseAction } from 'types/schema-generated-types';
import {
  BaseStudentFormEntry,
  StudentFormEntry,
  LicenseAssignFn,
  LicenseUnassignFn
} from '../../types';
import isValidStudent from '../../util/is-valid-student';
import {
  StudentFirstNameField,
  StudentGroupSelectField,
  StudentLastNameField,
  StudentUsernameField,
  StudentSecretCodeField,
  StudentEmailAddressField,
  StudentGradeSelectField
} from './StudentForm';

/*

  TODOS:
   - Update material table to rotate the icon a full 180 degrees.
   - Rewrite code once GraphQL connection is added.
*/
type StudentRowData = BaseStudentFormEntry & {
  secretCode: string;
  tableData: {
    id: number;
  };
};

interface AssignedProductsProps {
  rowData: StudentRowData;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const AssignedProducts: React.FC<AssignedProductsProps> = ({ rowData, onClick }) => {
  if (!rowData.tableData) return <div />;
  return <AssignProductsButton onClick={onClick} />;
};

interface StudentLicenseAssignmentProps {
  licenseUsage: OrganizationLicenseUsage[];
  productFamilyIds: number[];
  onLicenseAssign: LicenseAssignFn;
  onLicenseUnassign: LicenseUnassignFn;
  readonlyStudent?: boolean;
  readonlyGroup?: boolean;
}

function removeItemAtIndex(arr: Array<number>, index: number): Array<number> {
  return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

const StudentLicenseAssignment = ({
  licenseUsage,
  productFamilyIds,
  readonlyStudent = false,
  readonlyGroup = false,
  onLicenseAssign,
  onLicenseUnassign
}: StudentLicenseAssignmentProps) => {
  const classes = useStyles();
  const { getProductCurriculumDefaultGroupSettings } = useDefaultGroupSettings();
  const [{ productCache }] = useStore();

  const tableRef = useRef(null);
  const { values }: FormikProps<FormikValues> = useFormikContext();
  const [expanded, setExpanded] = useState<number[]>([]);

  const students = useMemo(() => {
    const formStudents: StudentFormEntry[] =
      values?.students ?? values?.ADD_STUDENTS?.students ?? [];
    return formStudents.map((student, index) => ({ id: index, ...student })).filter(isValidStudent);
  }, [values]);

  const tableData = useMemo(() => {
    const data: any[] = [];

    students?.forEach((student: StudentFormEntry, index: number) => {
      if (!student || !student.firstName || !student.secretCode) return;
      data.push({ ...student, secretCode: student.secretCode.code, tableData: { id: index } });
    });

    return data;
  }, [students]);

  const assignedProductFamilyStudentCurriculumMap = useMemo(() => {
    // Assigned Product Curriculums by Product Family ID and Student ID
    const result: Map<number, Map<number, number>> = new Map<number, Map<number, number>>();

    students.forEach(student => {
      student.licenses?.forEach(license => {
        const productFamilyMap = result.get(license.productFamilyId) ?? new Map<number, number>();
        if (!!license.productCurriculumId) {
          productFamilyMap.set(student.id, license.productCurriculumId);
        }

        result.set(license.productFamilyId, productFamilyMap);
      });
    });

    return result;
  }, [students]);

  const showGroupColumn =
    getProductCurriculumDefaultGroupSettings(
      students.flatMap(s => s.licenses?.map(l => l.productCurriculumId) ?? [])
    ).length > 0;

  const renderDetailPanel = useCallback(
    (rowData: any): ReactElement => (
      <>
        {productFamilyIds.map(productFamilyId => {
          const studentId = rowData.id;

          const handleLicenseAssign = (productFamilyId: number, productCurriculumId: number) =>
            onLicenseAssign(productFamilyId, productCurriculumId, studentId);

          const handleLicenseUnassign = (productFamilyId: number, productCurriculumId: number) =>
            onLicenseUnassign(productFamilyId, productCurriculumId, studentId);

          const assignedProductCurriculumMap = assignedProductFamilyStudentCurriculumMap.get(
            productFamilyId
          );

          const { pendingUpdates, otherPendingUpdates } = students.reduce(
            (acc, student) => {
              if (!student.licenses) return acc;

              const updates = student.licenses
                .filter(x => x.productFamilyId === productFamilyId)
                .map(x => ({
                  productFamilyId: x.productFamilyId,
                  productCurriculumId: x.productCurriculumId,
                  studentId: student.id,
                  action: LicenseAction.ASSIGN
                }));

              if (student.id === studentId) acc.pendingUpdates.push(...updates);
              else acc.otherPendingUpdates.push(...updates);

              return acc;
            },
            {
              pendingUpdates: [] as LicenseUpdate[],
              otherPendingUpdates: [] as LicenseUpdate[]
            }
          );

          return (
            <Container key={productFamilyId} maxWidth="lg" className={classes.cardContainer}>
              <StudentProductLicenseCard
                assignable
                productFamilyId={productFamilyId}
                assignedProductCurriculumId={assignedProductCurriculumMap?.get(studentId)}
                gradeLevel={rowData.gradeLevel}
                pendingUpdates={pendingUpdates}
                otherPendingUpdates={otherPendingUpdates}
                organizationLicenseUsage={licenseUsage}
                onLicenseAssign={handleLicenseAssign}
                onLicenseUnassign={handleLicenseUnassign}
              />
            </Container>
          );
        })}
      </>
    ),
    [
      assignedProductFamilyStudentCurriculumMap,
      classes,
      licenseUsage,
      productFamilyIds,
      students,
      onLicenseAssign,
      onLicenseUnassign
    ]
  );

  const handleAssignProductsClick = useCallback(
    (index: number, effect?: boolean) => (): void => {
      const i = expanded.indexOf(index);

      if (tableRef && tableRef.current) {
        if (effect && i >= 0) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore: Object is possibly 'null'.
          tableRef.current.onToggleDetailPanel([index], renderDetailPanel);
        }
        /*
          Because of neding to interact with refs and a bug within material-table
          relating to expanding multiple rows we need to simulate expansion in a specific
          order each render. 
        */
        if (!effect) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore: Object is possibly 'null'.
          tableRef.current.onToggleDetailPanel([index], renderDetailPanel);
          if (i >= 0) {
            setExpanded(removeItemAtIndex(expanded, i));
          } else {
            setExpanded([...expanded, index]);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableRef, renderDetailPanel, expanded, setExpanded]
  );

  const renderAssignedProducts = useCallback(
    (rowData: StudentRowData): ReactElement => {
      if (rowData.licenses?.length) {
        const products = rowData.licenses.reduce(
          (acc, { productFamilyId, productCurriculumId }) => {
            const product = productCache.findProductByProductCurriculumId(productCurriculumId);
            const productCurriculum = productCache.findProductCurriculumById(productCurriculumId);
            if (!product || !productCurriculum) return acc;
            return [...acc, { product, productCurriculum }];
          },
          [] as ProductWithCurriculum[]
        );

        return <ProductIconList products={products} />;
      }

      return (
        <AssignedProducts
          rowData={rowData}
          onClick={handleAssignProductsClick(rowData.tableData.id)}
        />
      );
    },
    [handleAssignProductsClick, productCache]
  );

  const renderColumn = (
    rowData: StudentRowData,
    fieldComponent: React.FC<any>,
    key: string,
    readonly = false,
    fallback?: string
  ): ReactElement => {
    const FieldComponent: FunctionComponent<any> = fieldComponent;
    const isExpanded = expanded.indexOf(rowData.tableData.id) >= 0;
    if (isExpanded && !readonly) {
      const indexes = students
        .map((student: StudentFormEntry, i: number) => {
          if (student.firstName) {
            return i;
          }
          return -1;
        })
        .filter((i: number) => i >= 0);

      const index = indexes[+rowData.tableData.id];

      return <FieldComponent index={index} touched={true} />;
    } else {
      const value = get(rowData, key);
      return <>{value ?? fallback}</>;
    }
  };

  const renderActions = useCallback(
    (rowData: StudentRowData): ReactElement => (
      <div style={{ textAlign: 'right' }}>
        <IconButton aria-label="delete" onClick={handleAssignProductsClick(rowData.tableData.id)}>
          <KeyboardArrowDownIcon />
        </IconButton>
      </div>
    ),
    [handleAssignProductsClick]
  );

  React.useEffect(() => {
    /* material-table is problematic and should be replaced 
      accessing expansion details or setting default states
      should not require messing with refs
    */

    expanded.forEach((index: number) => {
      handleAssignProductsClick(index, true)();
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students]);

  return (
    <Table
      tableRef={tableRef}
      detailPanel={renderDetailPanel}
      style={{}}
      columns={[
        {
          title: 'First Name',
          render: (rowData: StudentRowData): ReactElement =>
            renderColumn(rowData, StudentFirstNameField, 'firstName', readonlyStudent)
        },
        {
          title: 'Last Name',
          render: (rowData: StudentRowData): ReactElement =>
            renderColumn(rowData, StudentLastNameField, 'lastName', readonlyStudent)
        },
        {
          title: 'Username',
          render: (rowData: StudentRowData): ReactElement =>
            renderColumn(rowData, StudentUsernameField, 'username', readonlyStudent)
        },
        {
          title: 'Secret Code',
          render: (rowData: StudentRowData): ReactElement =>
            renderColumn(rowData, StudentSecretCodeField, 'secretCode', readonlyStudent)
        },
        {
          title: 'Email Address',
          render: (rowData: StudentRowData): ReactElement =>
            renderColumn(rowData, StudentEmailAddressField, 'email', readonlyStudent)
        },
        ...(showGroupColumn
          ? [
              {
                title: 'Group',
                render: (rowData: StudentRowData): ReactElement =>
                  renderColumn(rowData, StudentGroupSelectField, 'group.name', readonlyGroup, 'NA')
              }
            ]
          : []),
        {
          title: 'Grade',
          render: (rowData: StudentRowData): ReactElement =>
            renderColumn(rowData, StudentGradeSelectField, 'gradeLevel', readonlyStudent)
        },
        {
          title: 'Products',
          field: 'products',
          render: renderAssignedProducts
        },
        {
          title: '',
          field: '',
          render: renderActions
        }
      ]}
      data={tableData}
    />
  );
};

const useStyles = makeStyles(theme => ({
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  }
}));

export default StudentLicenseAssignment;
